import {
	Checkbox,
	DeprecatedCreativeModal as CreativeModal,
	DeprecatedScrollbar as Scrollbar,
	FlexColumn,
	FlexRow,
	SearchField,
	SubHeading,
	Text,
} from '@forecast-it/design-system';
import React, {useEffect, useState, useMemo} from 'react';
import styled from 'styled-components/macro';
import {useIntl} from 'react-intl';
import moment from 'moment';
import {showLoader, hideLoader} from '../../global_loader';
import Util from '../../../forecast-app/shared/util/util';
import CreateSageIntacctTimesheetsMutation from '../../../mutations/CreateSageIntacctTimesheetsMutation';
import {createFragmentContainer, graphql} from 'react-relay';
import type {SageIntacctExportTimesheetsModal_company$data as SageIntacctExportTimesheetsModal_company} from './__generated__/SageIntacctExportTimesheetsModal_company.graphql';
import {createDateFromYMD, dateObjectToIsoDate} from '../../../forecast-app/shared/util/DateUtil';
import {HorizontalBar} from './SageIntacctModal.styled';

type ListItemType = {
	hasBorderBottom?: boolean;
};

const scrollSectionHeight = window?.innerHeight < 850 ? '200px' : '500px';
const ListItem = styled.div<ListItemType>`
	width: 550px;
	padding: 18px 8px 18px 8px;
	border-bottom: ${({hasBorderBottom}) => (hasBorderBottom ? '1px solid #ccc' : 'none')};
`;

const ListWrapper = styled.div`
	height: ${scrollSectionHeight};
`;

type SageIntacctImportTimesheetsModalProps = {
	startDateMoment: moment.Moment;
	endDateMoment: moment.Moment;
	closeModal: () => void;
	company: SageIntacctExportTimesheetsModal_company;
};

type SageIntacctTimesheetEntry = {
	TimeRegId: string;
	Date: string | null;
	Hours: number;
	Billable: boolean | null;
	Notes: string | null;
	SageProjectId: string;
	SageTaskId: string | null | undefined;
	SageLocationId: string | null;
	SageDepartmentId: string | null | undefined;
};

type SageIntacctTimesheet = {
	SageEmployeeId: string;
	Name: string | null;
	StartDate: string;
	TimeRegs: SageIntacctTimesheetEntry[];
};

const SageIntacctExportTimesheetsModal = ({
	closeModal,
	startDateMoment,
	endDateMoment,
	company,
}: SageIntacctImportTimesheetsModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [timesheets, setTimesheets] = useState<SageIntacctTimesheet[]>([]);
	const [filteredTimesheets, setFilteredTimesheets] = useState<SageIntacctTimesheet[]>([]);
	const [selectedTimesheets, setSelectedTimesheets] = useState<string[]>([]);

	const selectAll = useMemo<boolean>(() => {
		if (filteredTimesheets.length === 0 || selectedTimesheets.length === 0) return false;

		return filteredTimesheets.every(ts => selectedTimesheets.includes(ts.SageEmployeeId));
	}, [filteredTimesheets, selectedTimesheets]);

	const [exporting, setExporting] = useState(false);
	const disableImportButton = exporting || selectedTimesheets.length === 0;

	useEffect(() => {
		// Map time regs as Sage timesheets
		const timesheets =
			company?.timeRegistrations?.edges?.reduce((acc: SageIntacctTimesheet[], cur) => {
				const timeReg = cur?.node;
				// if no person has no sage id or time reg already synched to Sage, skip
				if (!timeReg?.person?.sageIntacctId || timeReg.sageIntacctTimesheetEntryRecordNumber) {
					return acc;
				}
				const projectSageId: string | null | undefined =
					timeReg.project?.sageProject?.sageProjectId || timeReg.task?.project?.sageProject?.sageProjectId;
				// If project is not a sage project, or if time reg is on task with no sage id, skip.
				if (!projectSageId || (timeReg.task && !timeReg.task.sageIntacctId)) {
					return acc;
				}

				const entry: SageIntacctTimesheetEntry = {
					TimeRegId: timeReg.id,
					Date: dateObjectToIsoDate(
						createDateFromYMD({year: timeReg.year || 0, month: timeReg.month || 0, day: timeReg.day || 0})
					),
					Hours: (timeReg.minutesRegistered || 0) / 60.0,
					Billable: timeReg.billable,
					Notes: timeReg.notes,
					SageProjectId: projectSageId,
					SageTaskId: timeReg.task?.sageIntacctId,
					SageLocationId: timeReg.person.sageIntacctLocationId,
					SageDepartmentId: timeReg.person.department?.sageIntacctId,
				};
				if (timeReg) {
					const timesheet = acc.find(obj => obj.SageEmployeeId === timeReg?.person?.sageIntacctId);
					if (timesheet) {
						timesheet.TimeRegs.push(entry);
					} else {
						const timesheet: SageIntacctTimesheet = {
							SageEmployeeId: timeReg.person?.sageIntacctId || '',
							Name: timeReg.person.fullName,
							StartDate: startDateMoment.format('YYYY-MM-DD'),
							TimeRegs: [entry],
						};
						acc.push(timesheet);
					}
				}
				return acc;
			}, []) || [];

		setTimesheets(timesheets);
		setFilteredTimesheets(timesheets);
	}, []);

	const selectAllClick = () => {
		if (filteredTimesheets.length === 0) return;

		if (selectedTimesheets.length === 0) {
			setSelectedTimesheets(filteredTimesheets.map(ts => ts.SageEmployeeId));
		} else {
			const allFilteredIds = filteredTimesheets.map(ts => ts.SageEmployeeId);
			const isAllFilteredIdsInSelected = allFilteredIds.every(id => selectedTimesheets.includes(id));

			if (isAllFilteredIdsInSelected) {
				const removedSelectedIds = selectedTimesheets.filter(id => !allFilteredIds.includes(id));
				setSelectedTimesheets(removedSelectedIds);
			} else {
				const addedNewSelectedIds = selectedTimesheets.concat(allFilteredIds);
				setSelectedTimesheets(addedNewSelectedIds);
			}
		}
	};

	const timesheetClick = (inputId: string) => {
		if (selectedTimesheets.includes(inputId)) {
			const selectedDeps = selectedTimesheets.filter(id => id !== inputId);
			setSelectedTimesheets(selectedDeps);
		} else {
			const selectedDeps = [...selectedTimesheets];
			selectedDeps.push(inputId);
			setSelectedTimesheets(selectedDeps);
		}
	};

	const onFilterChange = (input: string) => {
		setFilteredTimesheets(timesheets.filter(tm => tm.Name?.toLowerCase().includes(input.toLowerCase())));
	};

	const onExportClick = () => {
		setExporting(true);
		showLoader();
		Util.CommitMutation(
			CreateSageIntacctTimesheetsMutation,
			{
				People: timesheets.filter(ts => selectedTimesheets.includes(ts.SageEmployeeId)),
			},
			() => {
				setExporting(false);
				closeModal();
				hideLoader();
			}
		);
	};

	return (
		<CreativeModal
			title={'Export Timesheets to Sage Intacct'}
			size={'l'}
			closeModal={closeModal}
			creativeButtonText={`Export Selected Timesheets (${selectedTimesheets.length})`}
			cancelButtonText={formatMessage({id: 'common.cancel'})}
			onCreateHandler={onExportClick}
			onCancelHandler={closeModal}
			disabled={disableImportButton}
			replicateDesignSystem
		>
			<FlexColumn gap={'xs'}>
				<Text color={Text.color.medium}>{`For the period ${startDateMoment.format(
					'DD MMM YYYY'
				)} to ${endDateMoment.format('DD MMM YYYY')}`}</Text>
				<SearchField isExpandable={false} onChange={onFilterChange} width="600px" />
			</FlexColumn>
			<ListItem>
				<FlexRow gap={'xl'}>
					<Checkbox onChange={selectAllClick} checked={selectAll} />
					<SubHeading size={'2'}>{'Select all'}</SubHeading>
				</FlexRow>
			</ListItem>
			<ListWrapper>
				<Scrollbar maxHeight={scrollSectionHeight}>
					<FlexColumn alignItems="flex-start" justifyContent="flex-start">
						{filteredTimesheets &&
							filteredTimesheets.map((dep: SageIntacctTimesheet) => {
								return (
									<ListItem key={'timesheet-' + dep.SageEmployeeId} hasBorderBottom>
										<FlexRow gap={'xl'}>
											<Checkbox
												onChange={() => timesheetClick(dep.SageEmployeeId)}
												checked={selectedTimesheets.includes(dep.SageEmployeeId)}
											/>
											<Text size={'2'}>{dep.Name}</Text>
										</FlexRow>
									</ListItem>
								);
							})}
					</FlexColumn>
				</Scrollbar>
			</ListWrapper>
			<HorizontalBar />
		</CreativeModal>
	);
};

const sageIntacctExportTimesheetsModalQuery = graphql`
	query SageIntacctExportTimesheetsModal_Query($startDateString: String, $endDateString: String) {
		viewer {
			actualPersonId
			component(name: "sage_intacct_export_timesheets_modal")
			company {
				...SageIntacctExportTimesheetsModal_company
					@arguments(startDateString: $startDateString, endDateString: $endDateString)
			}
		}
	}
`;

export {sageIntacctExportTimesheetsModalQuery};

export default createFragmentContainer(SageIntacctExportTimesheetsModal, {
	company: graphql`
		fragment SageIntacctExportTimesheetsModal_company on Company
		@argumentDefinitions(startDateString: {type: "String"}, endDateString: {type: "String"}) {
			timeRegistrations(first: 10000000, startDate: $startDateString, endDate: $endDateString) {
				edges {
					node {
						id
						year
						month
						day
						minutesRegistered
						billable
						person {
							id
							fullName
							sageIntacctId
							sageIntacctLocationId
							department {
								id
								sageIntacctId
							}
						}
						notes
						project {
							id
							name
							sageProject {
								sageProjectId
							}
						}
						task {
							id
							name
							sageIntacctId
							project {
								id
								name
								sageProject {
									sageProjectId
								}
							}
						}
						sageIntacctTimesheetEntryRecordNumber
					}
				}
			}
		}
	`,
});
